import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/shared";
import { FeaturedGlossary } from "@/components/FeaturedGlossary";
import { PROVINCES, labelFor } from "@/lib/constants";
import {
  MapPin, Bell, ArrowRight, ChevronRight, Coins, FileText, CalendarCheck,
  MessageCircleHeart, FolderClosed, Map, ListChecks, GraduationCap, ShieldCheck, Clock, Check, HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import "./dashboard.css";

const RING_C = 408;

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const { user, status, isPaid } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const load = async () => {
    const [roadmap, tasks, saved, docs, family, aa] = await Promise.all([
      api.get("/roadmaps/current").then((r) => r.data).catch(() => null),
      isPaid ? api.get("/tasks").then((r) => r.data).catch(() => []) : Promise.resolve([]),
      api.get("/saved-programs").then((r) => r.data).catch(() => []),
      isPaid ? api.get("/documents").then((r) => r.data).catch(() => []) : Promise.resolve([]),
      api.get("/family").then((r) => r.data).catch(() => null),
      isPaid ? api.get("/ask-anything/threads").then((r) => r.data.threads).catch(() => []) : Promise.resolve([]),
    ]);
    setData({ roadmap, tasks, saved, docs, family, aa });
    setLoading(false);
  };
  useEffect(() => { load(); }, [isPaid]);

  if (loading) return <Spinner />;

  const needsIntake = status.intake_status !== "completed" || !data.roadmap;
  const province = data.family?.province ? labelFor(PROVINCES, data.family.province) : "Ontario";
  const aaUnread = (data.aa || []).filter((t) => t.status === "sent").length;

  return (
    <div className="kp-dash">
      {/* TOP BAR */}
      <div className="top">
        <div>
          <div className="eyebrow"><span className="dot" /> Your dashboard</div>
          <h1 data-testid="dashboard-greeting">{greeting()}, {user?.first_name} 👋</h1>
          <p className="sub">
            {needsIntake
              ? "Let's build your family's starting plan — funding to review, documents to gather, and a gentle first step."
              : "Here's where your family's path stands today — funding to review, documents to gather, and your next gentle step."}
          </p>
        </div>
        <div className="top-right">
          <div className="chip" data-testid="dashboard-province"><MapPin /> {province}</div>
          <button className="iconbtn" aria-label="Notifications" data-testid="dashboard-notifications"
            style={{ position: "relative" }}
            onClick={() => aaUnread > 0 && navigate("/app/ask")}
            title={aaUnread > 0 ? `${aaUnread} KidPath ${aaUnread === 1 ? "reply" : "replies"} waiting` : undefined}>
            {(aaUnread > 0 || data.roadmap?.status === "stale") && <span className="ping" />}<Bell />
            {aaUnread > 0 && (
              <span data-testid="dashboard-notif-count"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-kid-coral text-white text-[10px] font-bold grid place-items-center leading-none">
                {aaUnread}
              </span>
            )}
          </button>
        </div>
      </div>

      {needsIntake ? <FirstVisit navigate={navigate} status={status} /> : <ActiveView data={data} isPaid={isPaid} reload={load} navigate={navigate} />}
    </div>
  );
}

/* ---------------- First-visit (empty) state ---------------- */
function FirstVisit({ navigate, status }) {
  const steps = [
    { t: "Tell us about your family", d: "Your child's age, province and where you are in the journey." },
    { t: "Get your starting roadmap", d: "Programs and documents that may be worth reviewing." },
    { t: "Take one clear next step", d: "We'll always show you a single, gentle thing to do next." },
  ];
  return (
    <div data-testid="dashboard-first-visit">
      <div className="hero-illus" data-testid="dashboard-hero-illustration">
        <img src="/assets/dashboard-hero-new.jpg" alt="A family walking together along a sunlit path in the park" />
      </div>
      <section className="welcome">
        <div className="cf"><span /><span /><span /><span /></div>
        <div className="wc-copy">
          <div className="eyebrow"><span className="dot" /> Let's begin</div>
          <h2>Let's map your family's path forward</h2>
          <p>Answer a few gentle questions about where you are today. We'll turn them into a starting plan for funding, documents, school supports and next steps.</p>
          <div className="mins"><Clock /> About 5 minutes · save and return any time</div>
          <button className="btn btn-primary" onClick={() => navigate("/app/onboarding")} data-testid="first-visit-start-intake">
            {status.intake_status === "in_progress" ? "Resume your intake" : "Start your intake"} <ArrowRight />
          </button>
        </div>
        <div className="wc-steps">
          {steps.map((s, i) => (
            <div className="wc-step" key={i}>
              <div className="n">{i + 1}</div>
              <div><div className="st">{s.t}</div><div className="sd">{s.d}</div></div>
            </div>
          ))}
        </div>
      </section>
      <div className="reassure" style={{ marginTop: 22 }}>
        <div className="i"><ShieldCheck /></div>
        <p><b>You are not alone. There is a path forward.</b> KidPath provides information and navigation only — never medical, legal, tax or financial advice.</p>
      </div>

      <FeaturedGlossary className="mt-6" />
    </div>
  );
}

/* ---------------- Ask Anything dashboard card ---------------- */
function AskAnythingCard({ threads, isPaid }) {
  if (!isPaid) return null;
  const list = threads || [];
  const replied = list.find((t) => t.status === "sent");
  const open = list.find((t) => t.status !== "closed");
  let variant, title, sub, to, cta;
  if (replied) {
    variant = "reply"; to = `/app/ask/${replied.id}`; cta = "Read reply";
    title = "KidPath replied 💬"; sub = replied.title;
  } else if (open) {
    variant = "waiting"; to = `/app/ask/${open.id}`; cta = "View";
    title = "We're on it"; sub = `We'll reply within 72 hours · ${open.title}`;
  } else {
    variant = "prompt"; to = "/app/ask"; cta = "Ask a question";
    title = "Ask Anything"; sub = "Questions, worries, wins or a tough day — send it to KidPath and hear back within 72 hours.";
  }
  const isReply = variant === "reply";
  return (
    <Link to={to} data-testid="dashboard-ask-card"
      className={`flex items-center gap-4 rounded-2xl p-5 mb-6 transition-colors ${isReply ? "bg-kid-navy text-white hover:bg-[#1a2b52]" : "kid-card hover:border-kid-teal"}`}>
      <div className={`w-12 h-12 rounded-2xl grid place-items-center shrink-0 ${isReply ? "bg-white/15 text-white" : "bg-kid-skytint text-kid-teal"}`}>
        <MessageCircleHeart size={22} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className={`font-extrabold ${isReply ? "text-white" : "text-kid-navy"}`}>{title}</p>
          {isReply && <span className="ping-badge inline-block w-2 h-2 rounded-full bg-kid-sun" data-testid="dashboard-ask-ping" />}
        </div>
        <p className={`text-sm truncate mt-0.5 ${isReply ? "text-white/75" : "text-kid-muted"}`}>{sub}</p>
      </div>
      <span className={`inline-flex items-center gap-1 text-sm font-poppins font-semibold shrink-0 ${isReply ? "text-white" : "text-kid-teal"}`} data-testid="dashboard-ask-cta">
        {cta} <ArrowRight size={16} />
      </span>
    </Link>
  );
}

/* ---------------- Active dashboard ---------------- */
function ActiveView({ data, isPaid, reload, navigate }) {
  const { roadmap, tasks, saved, docs } = data;
  const docsCount = docs?.length || 0;
  const completedTasks = (tasks || []).filter((t) => t.status === "completed").length;
  const openTasks = (tasks || []).filter((t) => t.status !== "completed");
  const dueSoon = openTasks.filter((t) => t.due_date && (new Date(t.due_date) - new Date()) / 86400000 <= 7).length;

  const journey = [
    { label: "Tell us about your family", status: "done" },
    { label: "Review supports that may fit", status: (saved?.length || 0) > 0 ? "done" : "now" },
    { label: isPaid ? "Gather your key documents" : "Explore your funding options", status: isPaid ? (docsCount > 0 ? "done" : "todo") : ((saved?.length || 0) > 0 ? "done" : "todo") },
    { label: "Take your next steps", status: completedTasks > 0 ? "done" : "todo" },
  ];
  const doneCount = journey.filter((s) => s.status === "done").length;
  const offset = RING_C * (1 - doneCount / journey.length);

  const toggleTask = async (t) => {
    const next = t.status === "completed" ? "not_started" : "completed";
    try { await api.patch(`/tasks/${t.id}`, { status: next }); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Fallback next-steps from roadmap high-priority items when no tasks
  const roadmapSuggestions = [];
  (roadmap?.sections || []).forEach((s) =>
    s.items.forEach((it) => { if (!it.locked && it.priority === "high" && roadmapSuggestions.length < 3) roadmapSuggestions.push(it); }));

  const stats = [
    { ic: Coins, bg: "var(--teal)", num: saved?.length || 0, unit: "saved", lbl: "Funding & credits to review", to: "/app/funding" },
    { ic: FileText, bg: "var(--sky)", num: isPaid ? docsCount : "—", unit: isPaid ? "documents" : "", lbl: "Documents gathered", to: "/app/documents" },
    { ic: CalendarCheck, bg: "var(--grape)", num: isPaid ? openTasks.length : "—", unit: isPaid ? "next steps" : "", lbl: dueSoon ? `${dueSoon} due within a week` : "Track your next steps", to: "/app/tasks" },
  ];

  const mods = [
    { ic: Coins, bg: "var(--teal)", h: "Funding & tax credits", p: "Guided help to understand the DTC, CDB and Ontario supports that may fit your family.", status: `${saved?.length || 0} to review`, to: "/app/funding" },
    { ic: MessageCircleHeart, bg: "var(--grape)", h: "AI Family Navigator", p: "Plain-language help to prepare questions, checklists and school emails — any time.", status: "Ready to help", to: "/app/navigator" },
    { ic: FolderClosed, bg: "var(--sky)", h: "Document tracker", p: "One calm binder for assessments, receipts, deadlines and forms — nothing lost.", status: isPaid ? `${docsCount} gathered` : "Family Path", locked: !isPaid, to: "/app/documents" },
    { ic: GraduationCap, bg: "var(--sun)", h: "School supports", p: "Prepare accommodation requests, questions and follow-ups for your next school meeting.", status: "In your roadmap", to: "/app/roadmap" },
    { ic: Map, bg: "var(--green)", h: "Support roadmap", p: "Your personalized map of programs, documents and next steps — updated as things change.", status: `${roadmap?.sections?.length || 0} sections`, to: "/app/roadmap" },
    { ic: ListChecks, bg: "var(--coral)", h: "Tasks & reminders", p: "Gentle, low-pressure nudges so nothing slips — only what matters, when it matters.", status: isPaid ? `${openTasks.length} open` : "Family Path", locked: !isPaid, to: "/app/tasks" },
  ];

  return (
    <div data-testid="dashboard-active-view">
      {/* HERO */}
      <div className="hero">
        <section className="road" data-testid="dashboard-roadmap-card">
          <div className="cf"><span /><span /><span /></div>
          <div className="road-top"><span className="tag">Your family roadmap</span></div>
          <div className="road-grid">
            <div className="ring">
              <svg viewBox="0 0 150 150" width="150" height="150">
                <defs><linearGradient id="rg" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#5AB4EE" /><stop offset="1" stopColor="#1FA5A0" />
                </linearGradient></defs>
                <circle className="track" cx="75" cy="75" r="65" />
                <circle className="prog" cx="75" cy="75" r="65" style={{ strokeDashoffset: offset }} />
              </svg>
              <div className="mid"><b>{doneCount}<span style={{ fontSize: "1.3rem", color: "var(--faint)" }}>/{journey.length}</span></b><small>steps done</small></div>
            </div>
            <div>
              <h2>You're making steady progress</h2>
              <p className="lede">Guided, one step at a time. You can pause and come back any time.</p>
              <ul className="steps-mini">
                {journey.map((s, i) => (
                  <li className={s.status} key={i}>
                    <span className="mk">{s.status === "done" ? "✓" : s.status === "now" ? <i /> : ""}</span>
                    <span>{s.label}</span>
                  </li>
                ))}
              </ul>
              <Link to="/app/roadmap" className="btn btn-primary" data-testid="dashboard-continue-roadmap">Continue your roadmap <ArrowRight /></Link>
            </div>
          </div>
        </section>

        <div className="stats">
          {stats.map((s, i) => (
            <button className="stat" key={i} onClick={() => navigate(s.to)} data-testid={`dashboard-stat-${i}`}>
              <div className="ic" style={{ background: s.bg }}><s.ic /></div>
              <div className="txt"><div className="num">{s.num} <small>{s.unit}</small></div><div className="lbl">{s.lbl}</div></div>
              <span className="go"><ChevronRight /></span>
            </button>
          ))}
        </div>
      </div>

      {/* NEXT STEPS */}
      <AskAnythingCard threads={data.aa} isPaid={isPaid} />
      <div className="sec-head">
        <h3>Your next steps</h3>
        <Link to="/app/tasks" className="link">View all tasks <ArrowRight /></Link>
      </div>
      <div className="next" data-testid="dashboard-next-steps">
        {isPaid && openTasks.length > 0 ? (
          openTasks.slice(0, 3).map((t) => (
            <div className={`task ${t.status === "completed" ? "checked" : ""}`} key={t.id} data-testid="dashboard-task">
              <div className={`check ${t.status === "completed" ? "on" : ""}`} role="checkbox" aria-checked={t.status === "completed"} onClick={() => toggleTask(t)} data-testid="dashboard-task-check">
                {t.status === "completed" && <Check size={14} color="#fff" strokeWidth={3} />}
              </div>
              <div className="body"><div className="t">{t.title}</div>{t.description && <div className="d">{t.description}</div>}</div>
              <div className="meta">
                {t.due_date && <span className="pill soon">Due {new Date(t.due_date).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}</span>}
                <span className="arrow"><ChevronRight /></span>
              </div>
            </div>
          ))
        ) : roadmapSuggestions.length > 0 ? (
          roadmapSuggestions.map((it) => (
            <Link to="/app/roadmap" className="task" key={it.item_id} data-testid="dashboard-suggestion">
              <div className="check" />
              <div className="body"><div className="t">{it.title}</div><div className="d">{it.description}</div></div>
              <div className="meta"><span className="pill calm">From your roadmap</span><span className="arrow"><ChevronRight /></span></div>
            </Link>
          ))
        ) : (
          <div className="task"><div className="check" /><div className="body"><div className="t">You're all caught up</div><div className="d">Open your roadmap to find your next recommended step.</div></div><Link to="/app/roadmap" className="meta"><span className="pill calm">Open roadmap</span></Link></div>
        )}
      </div>

      {/* MODULES */}
      <div className="sec-head">
        <h3>Explore your tools</h3>
        <Link to="/app/guide" className="link" data-testid="dashboard-about-tools"><HelpCircle style={{ width: 14, height: 14 }} /> About these tools</Link>
      </div>
      <div className="mods" data-testid="dashboard-modules">
        {mods.map((m, i) => (
          <Link to={m.to} className="mod" key={i} data-testid="dashboard-module">
            <div className="ic" style={{ background: m.bg }}><m.ic /></div>
            <h4>{m.h}</h4>
            <p>{m.p}</p>
            <div className="foot">
              <span className={`status ${m.locked ? "locked" : ""}`}><span className="dot" />{m.status}</span>
              <span className="open"><ArrowRight /></span>
            </div>
          </Link>
        ))}
      </div>

      {/* REASSURANCE */}
      <div className="reassure">
        <div className="i"><ShieldCheck /></div>
        <p><b>KidPath helps you understand and organize information and next steps.</b> It doesn't determine eligibility or guarantee funding — a qualified professional or government decision-maker makes those decisions.</p>
      </div>

      <FeaturedGlossary className="mt-6" />
    </div>
  );
}
